import { useRef, useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import {
  Download,
  FileText,
  Mail,
  MessageSquare,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmailPreview } from '@/components/workflow-builder/EmailPreview';
import { TemplateCodeEditor } from '@/components/workflow-builder/TemplateCodeEditor';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useCreateWorkflowTemplate,
  useDeleteWorkflowTemplate,
  useUpdateWorkflowTemplate,
  useWorkflowTemplates,
} from '@/hooks/useWorkflowTemplates';
import { extractErrorMessage, extractValidationErrors } from '@/lib/utils';
import { formatDate } from '@/utils/format';

import type { CreateWorkflowTemplateDto, WorkflowTemplate } from '@repo/shared-types';

// ---------------------------------------------------------------------------
// Modal form
// ---------------------------------------------------------------------------

interface TemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: WorkflowTemplate | null;
  onSubmit: (values: CreateWorkflowTemplateDto) => void;
  errors: Array<{ field: string; message: string }>;
  isLoading: boolean;
}

const TemplateModal = ({
  open,
  onOpenChange,
  template,
  onSubmit,
  errors,
  isLoading,
}: TemplateModalProps) => {
  const isEditing = template !== null;

  const [name, setName] = useState(template?.name ?? '');
  const [type, setType] = useState<'email' | 'sms'>(template?.type ?? 'email');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [bodyEditorOpen, setBodyEditorOpen] = useState(false);

  // Reset form when template changes (open/close)
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setName(template?.name ?? '');
      setType(template?.type ?? 'email');
      setSubject(template?.subject ?? '');
      setBody(template?.body ?? '');
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      type,
      subject: type === 'email' ? subject || null : null,
      body,
    });
  };

  const fieldError = (field: string) => errors.find((e) => e.field === field)?.message;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60" />
        <Dialog.Content className="bg-background fixed inset-0 z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <Dialog.Title className="text-lg font-semibold">
              {isEditing ? 'Edit workflow template' : 'New workflow template'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Form */}
          <div className="flex-1 overflow-y-auto p-6">
            <form
              id="template-form"
              onSubmit={handleSubmit}
              className="mx-auto max-w-2xl space-y-6"
            >
              {/* Name */}
              <div className="space-y-1">
                <label htmlFor="tpl-name" className="text-sm font-medium">
                  Name <span className="text-destructive">*</span>
                </label>
                <input
                  id="tpl-name"
                  className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Template name"
                  required
                />
                {fieldError('name') && (
                  <p className="text-destructive text-xs">{fieldError('name')}</p>
                )}
              </div>

              {/* Type */}
              <div className="space-y-1">
                <label htmlFor="tpl-type" className="text-sm font-medium">
                  Type <span className="text-destructive">*</span>
                </label>
                <select
                  id="tpl-type"
                  className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
                  value={type}
                  onChange={(e) => setType(e.target.value as 'email' | 'sms')}
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
                {fieldError('type') && (
                  <p className="text-destructive text-xs">{fieldError('type')}</p>
                )}
              </div>

              {/* Subject — email only */}
              {type === 'email' && (
                <div className="space-y-1">
                  <label htmlFor="tpl-subject" className="text-sm font-medium">
                    Subject
                  </label>
                  <input
                    id="tpl-subject"
                    className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email subject line"
                  />
                  {fieldError('subject') && (
                    <p className="text-destructive text-xs">{fieldError('subject')}</p>
                  )}
                </div>
              )}

              {/* Body */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="tpl-body" className="text-sm font-medium">
                    Body <span className="text-destructive">*</span>
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setBodyEditorOpen(true)}
                  >
                    Open Editor
                  </Button>
                </div>
                {body ? (
                  <div
                    className="border-input bg-muted/30 max-h-40 cursor-pointer overflow-hidden rounded-md border p-3 text-sm"
                    onClick={() => setBodyEditorOpen(true)}
                  >
                    <p className="text-muted-foreground line-clamp-6 font-mono text-xs">{body}</p>
                  </div>
                ) : (
                  <div
                    className="border-input text-muted-foreground cursor-pointer rounded-md border border-dashed p-6 text-center text-sm"
                    onClick={() => setBodyEditorOpen(true)}
                  >
                    Click to open the template editor...
                  </div>
                )}
                {fieldError('body') && (
                  <p className="text-destructive text-xs">{fieldError('body')}</p>
                )}
              </div>

              <TemplateCodeEditor
                value={body}
                onChange={setBody}
                type={type}
                subject={subject}
                onSubjectChange={setSubject}
                open={bodyEditorOpen}
                onOpenChange={setBodyEditorOpen}
              />

              {/* Preview — email only */}
              {type === 'email' && body && (
                <EmailPreview html={body} subject={subject} variables={{}} />
              )}

              {/* Generic errors */}
              {errors
                .filter((e) => !['name', 'type', 'subject', 'body'].includes(e.field))
                .map((e, i) => (
                  <p key={i} className="text-destructive text-xs">
                    {e.message}
                  </p>
                ))}
            </form>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t px-6 py-4">
            <Dialog.Close asChild>
              <Button variant="outline" disabled={isLoading}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button form="template-form" type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : isEditing ? 'Save changes' : 'Create template'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// ---------------------------------------------------------------------------
// Import helpers
// ---------------------------------------------------------------------------

interface ImportRow {
  name: string;
  type: string;
  subject?: string | null;
  body: string;
}

function isValidImportRow(row: unknown): row is ImportRow {
  if (typeof row !== 'object' || row === null) return false;
  const r = row as Record<string, unknown>;
  return (
    typeof r['name'] === 'string' && typeof r['type'] === 'string' && typeof r['body'] === 'string'
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const WorkflowTemplatesPage = () => {
  usePageTitle('Workflow Templates');

  const { data: templates = [], isLoading, error } = useWorkflowTemplates();
  const createMutation = useCreateWorkflowTemplate();
  const updateMutation = useUpdateWorkflowTemplate();
  const deleteMutation = useDeleteWorkflowTemplate();

  const [editingTemplate, setEditingTemplate] = useState<WorkflowTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowTemplate | null>(null);
  const [modalErrors, setModalErrors] = useState<Array<{ field: string; message: string }>>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ------ open/close modal ------

  const handleNew = () => {
    setEditingTemplate(null);
    setModalErrors([]);
    setIsModalOpen(true);
  };

  const handleEdit = (t: WorkflowTemplate) => {
    setEditingTemplate(t);
    setModalErrors([]);
    setIsModalOpen(true);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setModalErrors([]);
      setEditingTemplate(null);
    }
  };

  // ------ submit ------

  const handleSubmit = async (values: CreateWorkflowTemplateDto) => {
    try {
      setModalErrors([]);
      setIsSaving(true);

      if (editingTemplate) {
        await updateMutation.mutateAsync({ id: editingTemplate.id, data: values });
        toast({
          variant: 'success',
          title: 'Template updated',
          description: `"${values.name}" has been updated successfully.`,
        });
      } else {
        await createMutation.mutateAsync(values);
        toast({
          variant: 'success',
          title: 'Template created',
          description: `"${values.name}" has been created successfully.`,
        });
      }

      setIsModalOpen(false);
      setEditingTemplate(null);
      setModalErrors([]);
    } catch (err) {
      const validationErrors = extractValidationErrors(err);
      if (validationErrors.length > 0) {
        setModalErrors(validationErrors);
      } else {
        toast({
          variant: 'destructive',
          title: editingTemplate ? 'Failed to update template' : 'Failed to create template',
          description: extractErrorMessage(err),
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ------ delete ------

  const handleDelete = (t: WorkflowTemplate) => {
    setDeleteTarget(t);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({
        variant: 'success',
        title: 'Template deleted',
        description: `"${deleteTarget.name}" has been deleted.`,
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete template',
        description: extractErrorMessage(err),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // ------ export ------

  const handleExport = () => {
    const exportData = templates.map(({ name, type, subject, body }) => ({
      name,
      type,
      subject: subject ?? null,
      body,
    }));
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'workflow-templates.json';
    a.click();
    URL.revokeObjectURL(url);
    toast({
      variant: 'success',
      title: 'Exported',
      description: `${templates.length} template(s) downloaded.`,
    });
  };

  // ------ import ------

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-imported
    e.target.value = '';

    let rows: unknown[];
    try {
      const text = await file.text();
      rows = JSON.parse(text) as unknown[];
      if (!Array.isArray(rows)) throw new Error('JSON must be an array');
    } catch {
      toast({
        variant: 'destructive',
        title: 'Import failed',
        description: 'Could not parse JSON file.',
      });
      return;
    }

    const existingNames = new Set(templates.map((t) => t.name.toLowerCase()));
    let created = 0;
    let skipped = 0;
    let failed = 0;

    for (const row of rows) {
      if (!isValidImportRow(row)) {
        failed++;
        continue;
      }
      if (existingNames.has(row.name.toLowerCase())) {
        skipped++;
        continue;
      }
      try {
        await createMutation.mutateAsync({
          name: row.name,
          type: row.type === 'sms' ? 'sms' : 'email',
          subject: row.subject ?? null,
          body: row.body,
        });
        existingNames.add(row.name.toLowerCase());
        created++;
      } catch {
        failed++;
      }
    }

    toast({
      variant: created > 0 ? 'success' : 'destructive',
      title: 'Import complete',
      description: `Created: ${created}, Skipped (duplicate): ${skipped}, Failed: ${failed}`,
    });
  };

  // ------ render ------

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <FileText className="text-destructive mx-auto h-12 w-12 opacity-50" />
          <p className="text-destructive mt-4 font-medium">Failed to load workflow templates</p>
          <p className="text-muted-foreground mt-2 text-sm">{extractErrorMessage(error)}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading workflow templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page header */}
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Workflows</p>
          <h1 className="text-3xl font-bold tracking-tight">Workflow Templates</h1>
          <p className="text-muted-foreground max-w-3xl">
            Manage reusable email and SMS templates for workflow automation steps.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleImportClick}>
            <Upload className="h-4 w-4" />
            Import
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={templates.length === 0}>
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4" />
            New Template
          </Button>
        </div>
      </header>

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle>Workflow Templates</CardTitle>
          <CardDescription>
            Reusable email and SMS templates used in automated workflow steps.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-muted-foreground flex min-h-[200px] flex-col items-center justify-center space-y-4 py-8 text-center">
              <FileText className="h-12 w-12 opacity-50" />
              <div>
                <p className="font-medium">No workflow templates yet</p>
                <p className="text-sm">Create your first template or import from a JSON file.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {templates.map((t) => (
                <div key={t.id} className="rounded-lg border p-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold">{t.name}</p>
                        <Badge
                          variant={t.type === 'email' ? 'default' : 'secondary'}
                          className="gap-1"
                        >
                          {t.type === 'email' ? (
                            <Mail className="h-3 w-3" />
                          ) : (
                            <MessageSquare className="h-3 w-3" />
                          )}
                          {t.type === 'email' ? 'Email' : 'SMS'}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground mt-0.5 text-sm">
                        {t.subject ? (
                          <>
                            <span className="font-medium">Subject:</span> {t.subject} &middot;{' '}
                          </>
                        ) : null}
                        Created {formatDate(t.created_at)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(t)}>
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(t)}>
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                      Body preview
                    </p>
                    <p className="text-muted-foreground mt-1 line-clamp-3 text-sm">{t.body}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit modal — key forces remount so state resets on template change */}
      <TemplateModal
        key={editingTemplate?.id ?? 'new'}
        open={isModalOpen}
        onOpenChange={handleModalOpenChange}
        template={editingTemplate}
        onSubmit={handleSubmit}
        errors={modalErrors}
        isLoading={isSaving}
      />

      {/* Delete confirmation */}
      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        resourceName="workflow template"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
